import { createSelector } from 'react-sweet-state';

import { ValidationFieldSet, type Validators } from '../../../../common/types/field-validation';
import type { FieldId, FormField } from '../../../../common/types/form/form-fields';
import type { FieldValue } from '../../../../common/types/form/form-fields/field-values';
import type { FormRendererState } from '../../types';
import { currentValueSelector } from '../current-value';
import { fieldValidationEnabledSelector } from '../field-validation-enabled';

export interface FieldValidationErrorsSelectorProps {
	fieldId: FieldId;
}

const getValidationErrorsForField = (
	field: FormField,
	defaultValidators: Validators,
	currentValue?: FieldValue,
	validators?: Validators,
) => {
	const fieldType = field.type;
	const validator = validators?.[fieldType] ?? defaultValidators[fieldType];
	if (validator) {
		return validator(field, defaultValidators, currentValue);
	}
	return [];
};

export const fieldValidationErrorsSelector: import('react-sweet-state').OutputSelector<
	FormRendererState,
	FieldValidationErrorsSelectorProps,
	import('../../../../common/types/field-validation').ValidationError[]
> = createSelector(
	(state: FormRendererState) => ({
		formFields: state.formFields,
		fieldValidation: state.fieldValidation,
		defaultValidators: state.defaultValidators,
		securityCriticalValidators: state.securityCriticalValidators,
	}),
	(_, props: FieldValidationErrorsSelectorProps) => props.fieldId,
	currentValueSelector,
	fieldValidationEnabledSelector,
	(
		{ formFields, fieldValidation, defaultValidators, securityCriticalValidators },
		fieldId,
		currentValue,
		validationEnabled,
	) => {
		if (!validationEnabled) {
			return [];
		}
		const field = formFields[fieldId];
		if (field === undefined) {
			return [];
		}

		const isSecurityCritical =
			fieldValidation.validationTargets?.fieldSet === ValidationFieldSet.SECURITY_CRITICAL;

		return getValidationErrorsForField(
			field,
			defaultValidators,
			currentValue,
			isSecurityCritical ? securityCriticalValidators : fieldValidation.validators,
		);
	},
);
